import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useMatches } from "react-router";
import { useMutation } from "@apollo/client/react";
import {
  useUser,
  useHasEnrolledPrograms,
  useCurrentSpace,
} from "~/hooks/context-hook";
import { useConfig } from "~/hooks/webconfig";
import { graphql } from "~/graphql/codegen";
import {
  AiCoachAvatarClientEventCode,
  AiCoachAvatarReportOutcome,
  AiCoachAvatarReportPhase,
} from "~/graphql/codegen/graphql";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { LiveAvatarCoach } from "@mindstonehq/ui/ui-custom/live-avatar-coach";
import {
  deriveCoachCardLocator,
  useCoachPageContext,
} from "~/components/coach-page-context";
import coachAvatarWebp from "~/assets/coach-avatar.webp";
import type {
  LiveAvatarClientEvent,
  LiveAvatarClientEventCode,
  LiveAvatarCoachCopy,
  LiveAvatarCoachReport,
  LiveAvatarCoachReportOutcome,
  LiveAvatarCoachReportPhase,
  LiveAvatarSessionRequest,
  LiveAvatarSessionToken,
} from "@mindstonehq/ui/ui-custom/live-avatar-coach";

// `$mindspaceID` (required since 2026-09-02) is the space the learner is
// BROWSING — it gives the server access/context and tells it whose
// `liveAvatarCoachEnabled` setting to read. It is NOT an enrollment scope, so
// a learner enrolled only in space A keeps the coach while browsing an
// accessible, enabled space B.
//
// `$input` stays optional (card-coach Amendment 1-A): absent ⇒ DEFAULT mode;
// a card locator ⇒ card-walkthrough mode. The server still derives everything
// else — the ids only SELECT which server-derived context to build, validated
// against the caller's own enrollment.
const AI_COACH_AVATAR_SESSION_MUTATION = graphql(`
  mutation AiCoachAvatarSession($mindspaceID: ID!, $input: AiCoachAvatarSessionInput) {
    aiCoachAvatarSession(mindspaceID: $mindspaceID, input: $input) {
      sessionToken
      sessionId
      maxSessionDurationSec
      isSandbox
    }
  }
`);

// Session-outcome report (Amendment 6). Load-bearing, not just telemetry: the
// server releases the user's 660s avatar-coach lease when it accepts this, so
// without it a learner who closes the panel is locked out of their next
// session with `live_avatar_coach_active_lease` for up to eleven minutes.
const AI_COACH_AVATAR_REPORT_MUTATION = graphql(`
  mutation AiCoachAvatarReport($input: AiCoachAvatarReportInput!) {
    aiCoachAvatarReport(input: $input)
  }
`);

const AI_COACH_AVATAR_CLIENT_EVENT_MUTATION = graphql(`
  mutation AiCoachAvatarClientEvent($input: AiCoachAvatarClientEventInput!) {
    aiCoachAvatarClientEvent(input: $input)
  }
`);

// The widget reports phase/outcome as string unions; the schema types them as
// codegen enums. The runtime values are identical, but a string literal is not
// assignable to an enum member — and a cast would un-check the boundary. These
// exhaustive Records ARE the check: a value added or removed on either side
// fails to compile right here.
const REPORT_OUTCOME_INPUT: Record<
  LiveAvatarCoachReportOutcome,
  AiCoachAvatarReportOutcome
> = {
  USER_CLOSE: AiCoachAvatarReportOutcome.UserClose,
  IDLE: AiCoachAvatarReportOutcome.Idle,
  MAX_DURATION: AiCoachAvatarReportOutcome.MaxDuration,
  AGENT_HANGUP: AiCoachAvatarReportOutcome.AgentHangup,
  SANDBOX: AiCoachAvatarReportOutcome.Sandbox,
  UNEXPECTED: AiCoachAvatarReportOutcome.Unexpected,
  NO_CREDITS: AiCoachAvatarReportOutcome.NoCredits,
  NETWORK_ERROR: AiCoachAvatarReportOutcome.NetworkError,
};

const REPORT_PHASE_INPUT: Record<
  LiveAvatarCoachReportPhase,
  AiCoachAvatarReportPhase
> = {
  ENDED: AiCoachAvatarReportPhase.Ended,
  START_FAILED: AiCoachAvatarReportPhase.StartFailed,
};

const CLIENT_EVENT_CODE_INPUT: Record<
  LiveAvatarClientEventCode,
  AiCoachAvatarClientEventCode
> = {
  AUTOPLAY_BLOCKED: AiCoachAvatarClientEventCode.AutoplayBlocked,
  BARGE_IN_FALLBACK_FIRED: AiCoachAvatarClientEventCode.BargeInFallbackFired,
  CLIENT_ERROR_OTHER: AiCoachAvatarClientEventCode.ClientErrorOther,
  CONNECTION_QUALITY_BAD: AiCoachAvatarClientEventCode.ConnectionQualityBad,
  DATA_CHANNEL_SILENT: AiCoachAvatarClientEventCode.DataChannelSilent,
  KEEPALIVE_FAILED: AiCoachAvatarClientEventCode.KeepaliveFailed,
  MEDIA_PLAYBACK_STALLED: AiCoachAvatarClientEventCode.MediaPlaybackStalled,
  MIC_DENIED_PREFLIGHT: AiCoachAvatarClientEventCode.MicDeniedPreflight,
  MIC_DEVICE_SWITCHED: AiCoachAvatarClientEventCode.MicDeviceSwitched,
  MIC_DEVICE_SWITCH_FAILED: AiCoachAvatarClientEventCode.MicDeviceSwitchFailed,
  MIC_PROMPT_DISMISSED: AiCoachAvatarClientEventCode.MicPromptDismissed,
  MIC_SILENT: AiCoachAvatarClientEventCode.MicSilent,
  MIC_UNAVAILABLE_POST_START: AiCoachAvatarClientEventCode.MicUnavailablePostStart,
  RESUME_FAILED_ACTIVE_LEASE: AiCoachAvatarClientEventCode.ResumeFailedActiveLease,
  RESUME_FAILED_GLOBAL_CAPACITY:
    AiCoachAvatarClientEventCode.ResumeFailedGlobalCapacity,
  RESUME_FAILED_OTHER: AiCoachAvatarClientEventCode.ResumeFailedOther,
  RESUME_FAILED_RATE_LIMITED: AiCoachAvatarClientEventCode.ResumeFailedRateLimited,
  RESUME_STARTED: AiCoachAvatarClientEventCode.ResumeStarted,
  RESUME_SUCCEEDED: AiCoachAvatarClientEventCode.ResumeSucceeded,
  SDK_IMPORT_FAILED: AiCoachAvatarClientEventCode.SdkImportFailed,
  SESSION_DISCONNECTED_UNEXPECTED:
    AiCoachAvatarClientEventCode.SessionDisconnectedUnexpected,
  TEXT_SEND_FAILED: AiCoachAvatarClientEventCode.TextSendFailed,
  VIDEO_ATTACH_FAILED: AiCoachAvatarClientEventCode.VideoAttachFailed,
};

/**
 * Thin per-app wrapper for the shared LiveAvatarCoach widget (web.space).
 *
 * Mirrors web.app/app/components/live-avatar-coach-mount.tsx — kept parallel
 * and consistent per Stage 5. Differences:
 *  - Auth context: web.space uses MainContext's `user` (via `useUser()` from
 *    ~/hooks/context-hook) and `authenticated` flag, not web.app's UserContext.
 *  - graphql import: web.space re-exports from ~/graphql/codegen.
 *  - Card-coach wiring (Amendment 2.1, web.space only): the mint-time
 *    route-derived card input (see getSessionToken below) and the program-day
 *    panel offset / focus-trap opt-out. web.app keeps the plain general-coach
 *    mount (zero-arg getSessionToken, no new props).
 *
 * Stage 8 (2026-08-11): the GrowthBook flag gate was dropped per user
 * decision "don't build a flag, just deploy to staging". The sole on/off
 * switch is the server-side `LIVE_AVATAR_COACH_ENABLED` env kill-switch.
 *
 * Enrollment gate (2026-08-17): the launcher only renders for users enrolled
 * in at least one program cohort ANYWHERE (MainContext.hasEnrolledPrograms,
 * computed across `mindspaces.all`) — the coach is a program-learner feature,
 * and the server enforces the same rule on `aiCoachAvatarSession`. It is NOT
 * scoped to the space being browsed.
 *
 * Per-space capability (2026-09-02): the mint now carries the current space's
 * id, and the launcher hides when that space's
 * `modules.liveAvatarCoachEnabled` is explicitly `false`. This replaces the
 * 2026-08-28 self-serve suppression, which is gone from both halves of the
 * rule — see useLiveAvatarCoachGate.
 *
 * Hydration safety: the `mounted` state ensures SSR and the first client
 * render agree (render nothing), then the launcher shell mounts after the
 * effect — mirroring the hubspot-initializer pattern and preserving the
 * widget's own hydration invariant (Amendment 8).
 *
 * Launcher placement (UX call, 2026-08-14): the collapsed launcher is
 * portalled via `launcherSlotSelector` into the right-edge rail that also
 * holds the "Chat to us" support tab (components/hubspot-initializer.tsx),
 * where it renders as a matching vertical tab stacked above it with a gap.
 *
 * This retires the 2026-08-12 prod hotfix that suppressed the coach under
 * `/programs/*` (route unmount, then Stage-5 `hideLauncher`): the floating
 * launcher was `fixed bottom-5 right-5 z-40` and the program-day nav bar is
 * `fixed bottom-0 … z-10`
 * (routes/_app.programs.$slug.$cohortID.$sectionID.$dayID/
 * program-day-nav-buttons.tsx), so the pill landed inside the bar, over the
 * Next button, and intercepted its clicks at every viewport width — blocking
 * progression through the whole program flow. A vertically-centred right-edge
 * tab cannot collide with a bottom bar, and unlike a top-bar placement it does
 * not depend on the route rendering any chrome — which matters because the
 * program-day START card renders no header at all. The coach tab is therefore
 * back on program pages, and it is the SOLE entry point (Amendment 2.1 — the
 * inline card invitation was retired after the user's s101 placement verdict):
 * card mode is derived from the current route at mint time instead of being
 * carried by an imperative open(request).
 *
 * The rail is rendered app-wide by HubSpotInitializer (root.tsx), so the slot
 * exists on every route; the widget's floating-pill fallback is effectively
 * unreachable here and stays only as a safety net.
 *
 * The EXPANDED desktop panel must not defeat a page's own bottom action bar
 * either (final review F1/F3; FOX-6892): the default panel (`bottom-4`, z-40,
 * 440px wide) sits on top of the right-aligned forward button of the
 * program-day nav bar and of the personaliser footer, and its focus trap keeps
 * Tab inside the panel. So on every route the panel opens above the bar and
 * does not trap Tab — see BOTTOM_BAR_PANEL_OFFSET below. It is
 * `aria-modal="false"` already. The open HubSpot chat takes the same
 * clearance (app/tailwind.css). The mobile Sheet stays modal-by-design
 * (dismiss to navigate).
 */
const COACH_LAUNCHER_SLOT = "[data-coach-launcher-slot]";

// Clearance for a page's bottom action bar. The program-day nav bar
// (program-day-nav-buttons.tsx:149) is `fixed bottom-0 … z-10`, 1px border-t +
// `sm:py-6` (48px) + `size="lg"` buttons (`h-10`, 40px) = 89px tall at every
// width the DESKTOP panel renders at; the personaliser footer
// (routes/personalizer/route.tsx) is built the same way and is as tall.
// (useIsMobile splits at 768px, above the 640px `sm:` step; below 768px the
// mobile Sheet is used instead.) `bottom-24` (96px) clears both with margin. The
// height caps shrink in step (6rem bottom + 1rem top = 7rem) so the raised
// panel can never overflow the viewport top on short screens. tailwind-merge
// drops the conflicting defaults (`bottom-4`, its safe-area twin, `h-…`,
// `max-h-…`).
const BOTTOM_BAR_PANEL_OFFSET =
  "bottom-24 [bottom:calc(6rem+env(safe-area-inset-bottom))] " +
  "h-[min(600px,calc(100dvh-7rem))] max-h-[calc(100dvh-7rem)]";

/**
 * The current space's `liveAvatarCoachEnabled` capability, read defensively.
 *
 * `!== false`, NOT truthiness. This is a TRUE-DEFAULT setting: a space that
 * has no value yet, a `modules` selection that predates the field, or a
 * partial payload must all still mount the launcher. `!!value` or `?? false`
 * would switch the coach off for every existing Live space the moment a
 * carrier lags the schema.
 *
 * The field is a non-null `Boolean!` on `Mindspace.modules` and is selected by
 * the `SpaceMetadata` fragment in root.tsx, so the real value arrives here.
 * The parameter is still typed `unknown` and read defensively on purpose: a
 * cached Apollo entry written before the field existed, or any other partial
 * payload, must degrade to ENABLED rather than silently unmounting the coach
 * for a Live space.
 */
export function spaceCoachCapabilityEnabled(modules: unknown): boolean {
  if (typeof modules !== "object" || modules === null) return true;
  return (modules as Record<string, unknown>).liveAvatarCoachEnabled !== false;
}

export interface LiveAvatarCoachGate {
  /** Whether the launcher should render at all. */
  enabled: boolean;
  /** The space to mint against — "" when there is no current space. */
  mindspaceID: string;
}

/**
 * The client-side launcher formula, as a hook so it can be exercised through
 * the real contexts (see live-avatar-coach-mount.spec.tsx).
 *
 *   global config kill-switch
 *   AND any-programme enrollment ANYWHERE
 *   AND a current Mindspace exists
 *   AND currentSpace.modules.liveAvatarCoachEnabled !== false
 *
 * ⚠️  `hasEnrolledPrograms` is computed across `mindspaces.all` in root.tsx and
 * STAYS that way. It is not scoped to the current space: a learner enrolled
 * only in space A must keep the launcher while browsing an accessible, enabled
 * space B. The server enforces the same rule and is the authoritative stop;
 * this gate is only a UX optimisation that avoids showing a button that would
 * throw on click.
 *
 * The 2026-08-28 self-serve suppression (`user.selfServePolicy === true`) is
 * GONE, matching the server: the self-serve pilot needs the coach ON, and the
 * switch is now the per-Mindspace capability.
 */
export function useLiveAvatarCoachGate(): LiveAvatarCoachGate {
  const user = useUser();
  const hasEnrolledPrograms = useHasEnrolledPrograms();
  const currentSpace = useCurrentSpace();
  const config = useConfig();

  const mindspaceID = currentSpace?.id ?? "";

  return {
    enabled:
      !!user &&
      config.liveAvatarCoachEnabled &&
      hasEnrolledPrograms &&
      // No current Mindspace ⇒ do not mount and do not mint. The mutation
      // requires a target and there is nothing honest to send.
      mindspaceID !== "" &&
      spaceCoachCapabilityEnabled(currentSpace?.modules),
    mindspaceID,
  };
}

export function LiveAvatarCoachMount() {
  const gate = useLiveAvatarCoachGate();
  const location = useLocation();
  const { t } = useLingui();
  const [mounted, setMounted] = useState(false);
  const [mintSession] = useMutation(AI_COACH_AVATAR_SESSION_MUTATION);
  const [reportSessionMutation] = useMutation(AI_COACH_AVATAR_REPORT_MUTATION);
  const [reportClientEventMutation] = useMutation(
    AI_COACH_AVATAR_CLIENT_EVENT_MUTATION,
  );
  // "Where is the learner and what are they looking at" — built from the
  // router match tree (loader data), sent to the agent as a silent contextual
  // update once live and on every navigation. See coach-page-context.ts.
  const pageContext = useCoachPageContext();
  const matches = useMatches();

  // Amendment 2.1: the docked rail tab is the sole entry point, so card mode
  // is derived from WHERE the learner opens the coach — on a day content card
  // this is that card's mint locator, everywhere else null (DEFAULT mint).
  // Recomputed on navigation; the widget's start path reads the latest
  // getSessionToken closure, so the mint always sees the route it happens on.
  const routeCard = useMemo(
    () => deriveCoachCardLocator(matches, location.pathname),
    [matches, location.pathname],
  );

  // Post-mount gate: SSR and first client render both render nothing, so
  // there is no hydration mismatch. Auth context is only read after mount.
  useEffect(() => {
    setMounted(true);
  }, []);

  // The kill-switch, enrollment, current-space and per-space capability terms
  // all live in `useLiveAvatarCoachGate` above (with the reasoning for each);
  // `mounted` is the hydration half and is the only term that belongs here.
  //
  // The kill-switch term is LOAD-BEARING, not cosmetic: `aiCoachAvatarSession`
  // refuses to mint when `LIVE_AVATAR_COACH_ENABLED !== "true"`, so rendering
  // the launcher without it produces a button that throws on click — exactly
  // why the 2026-08-13 prod incident needed a code change to unmount the
  // widget instead of an env flip.
  // Not route-gated in general (see below), except the Rebel launch bridge,
  // which is a single-purpose page (open-in-rebel-learning plan).
  const onLaunchBridge = location.pathname.startsWith("/launch/");
  const renderWidget = mounted && gate.enabled && !onLaunchBridge;

  const getSessionToken = useCallback(
    async (
      request?: LiveAvatarSessionRequest,
    ): Promise<LiveAvatarSessionToken> => {
      // An explicit request (an imperative open(request), none in-repo today)
      // would win; otherwise the route-derived locator decides the mode. With
      // neither, `input` stays undefined and the serialized wire shape is
      // byte-identical to the pre-card-mode zero-arg call (DEFAULT mint).
      const card = request?.card ?? routeCard;
      const result = await mintSession({
        variables: {
          // The space the learner is browsing. The gate never renders the
          // launcher without one, so this is non-empty by construction.
          mindspaceID: gate.mindspaceID,
          input: card ? { card } : undefined,
        },
      });
      const data = result.data?.aiCoachAvatarSession;
      if (!data) {
        throw new Error("aiCoachAvatarSession mutation returned no data");
      }
      return {
        sessionToken: data.sessionToken,
        sessionId: data.sessionId,
        maxSessionDurationSec: data.maxSessionDurationSec,
        isSandbox: data.isSandbox,
      };
    },
    [mintSession, routeCard, gate.mindspaceID],
  );

  const reportSession = useCallback(
    async (report: LiveAvatarCoachReport) => {
      // The hook keeps normal teardown best-effort, but Stage 8a's awaited
      // resume report must see rejections so it can distinguish "lease already
      // gone" from "lease may still be held".
      await reportSessionMutation({
        variables: {
          input: {
            sessionId: report.sessionId,
            phase: REPORT_PHASE_INPUT[report.phase],
            outcome: REPORT_OUTCOME_INPUT[report.outcome],
          },
        },
      });
    },
    [reportSessionMutation],
  );

  const reportClientEvent = useCallback(
    async (event: LiveAvatarClientEvent) => {
      const result = await reportClientEventMutation({
        variables: {
          input: {
            code: CLIENT_EVENT_CODE_INPUT[event.code],
            claimedSessionId: event.sessionId,
            occurredAt: event.occurredAt,
          },
        },
      });
      return result.data?.aiCoachAvatarClientEvent === true;
    },
    [reportClientEventMutation],
  );

  const onError = useCallback((error: unknown) => {
    // Non-fatal: the widget owns its own error surface. Log for telemetry;
    // the shared component already renders the designed error state.
    if (typeof console !== "undefined" && console.error) {
      console.error("[live-avatar-coach]", error);
    }
  }, []);

  // Gate: post-mount + auth + kill-switch + enrollment-anywhere + a current
  // space whose coach capability is not explicitly off (see
  // useLiveAvatarCoachGate). SSR renders nothing. NOT route-gated — for an
  // enrolled learner the rail tab shows everywhere, including /programs/* (a
  // right-edge tab cannot collide with the bottom nav bar by construction).
  if (!renderWidget) return null;

  // All LiveAvatarCoachCopy keys (compiler-enforced). Lingui macro
  // `t(msg\`...\`)` extracts to the app's catalog; the exact English source
  // strings are documented on LiveAvatarCoachCopy.
  const copy: LiveAvatarCoachCopy = {
    launcherLabel: t(msg`Ask your AI coach`),
    panelTitle: t(msg`Mindstone Coach`),
    loading: t(msg`Loading coach…`),
    micPrompt: t(
      msg`Your coach needs to hear you. We'll ask for microphone access next — please tap Allow.`,
    ),
    micContinueWithText: t(msg`Continue with text only`),
    micUnavailableNotice: t(
      msg`We can't pick up your microphone right now — you can type your side of the conversation instead.`,
    ),
    dataChannelNotice: t(
      msg`The coach connection is degraded. Voice may still work, but Stop and typing stay reliable.`,
    ),
    micDiagnosticTrigger: t(msg`Check microphone`),
    micSilentNotice: t(
      msg`We are not picking up sound from your microphone. Check it is unmuted or selected in your device settings, or type below.`,
    ),
    micLevelLabel: t(msg`Microphone input level`),
    micDevicePickerLabel: t(msg`Microphone`),
    micDeviceSwitchFailed: t(msg`That microphone didn't work. Try another one.`),
    connecting: t(msg`Connecting…`),
    listening: t(msg`Coach is listening`),
    userSpeaking: t(msg`You're speaking`),
    coachSpeaking: t(msg`Coach is speaking`),
    reconnecting: t(msg`Reconnecting…`),
    connectionUnstable: t(msg`Connection is unstable. Hang tight.`),
    resumeFailedIntro: t(
      msg`Your conversation was interrupted and we could not reconnect.`,
    ),
    resumeFailedRateLimited: t(
      msg`Reconnecting counts toward your three sessions per hour, and you have reached that limit. Your transcript is saved above. Please try again later.`,
    ),
    reconnectingComposerHint: t(msg`Reconnecting. Your draft will stay here.`),
    tapForSound: t(msg`Tap for sound`),
    cantHearCoach: t(msg`Can't hear the coach?`),
    cantHearCoachHint: t(
      msg`We'll try sound again. You can keep following the transcript above.`,
    ),
    audioOutputPickerLabel: t(msg`Audio output`),
    audioOutputPickerPlaceholder: t(msg`Choose speaker`),
    audioOutputFallbackLabel: t(msg`Audio output`),
    audioOutputSystemDefault: t(msg`System default`),
    audioOutputSwitchFailed: t(
      msg`We couldn't switch speakers. Check your browser or system sound settings.`,
    ),
    mediaPlaybackStalledNotice: t(
      msg`The coach video paused. You can keep following the transcript while we try playback again.`,
    ),
    mute: t(msg`Mute microphone`),
    unmute: t(msg`Unmute microphone`),
    end: t(msg`End conversation`),
    close: t(msg`Close coach`),
    retry: t(msg`Try again`),
    micDeniedTitle: t(msg`Microphone access is blocked`),
    micDeniedDescription: t(
      msg`Allow microphone access in your browser settings, then try again.`,
    ),
    rateLimited: t(
      msg`You can start up to three conversations per hour. Please try again later.`,
    ),
    activeLease: t(
      msg`Your previous conversation is still closing. Try again in a few minutes.`,
    ),
    globalCapacity: t(msg`The coach is busy right now. Try again shortly.`),
    temporarilyUnavailable: t(
      msg`The coach is temporarily unavailable. Please try again later.`,
    ),
    noCredits: t(
      msg`The coach is temporarily unavailable. Please try again later.`,
    ),
    startFailed: t(
      msg`The coach is temporarily unavailable. Please try again later.`,
    ),
    endedUserClose: t(msg`Conversation ended`),
    endedIdle: t(msg`Conversation ended after inactivity`),
    endedMaxDuration: t(msg`This conversation reached the 10-minute limit`),
    endedAgentHangUp: t(msg`The coach ended the conversation`),
    endedSandbox: t(msg`Test conversation ended`),
    endedUnexpected: t(msg`The connection ended unexpectedly`),
    maxDurationWarning: t(msg`This conversation will end in 1 minute.`),
    inputPlaceholder: t(msg`Type a message (or just talk)…`),
    send: t(msg`Send`),
    typeInstead: t(msg`or type instead — no microphone needed`),
    textSendFailed: t(msg`Message didn't send. Try again.`),
    genericError: t(
      msg`Sorry — we couldn't start the conversation. Please try again.`,
    ),
    transcriptLabel: t(msg`Transcript`),
    showTranscript: t(msg`Show transcript`),
    hideTranscript: t(msg`Hide transcript`),
    headerConnecting: t(msg`connecting`),
    headerListening: t(msg`listening`),
    headerSpeaking: t(msg`speaking`),
    coachTurnAria: t(msg`Coach said`),
    userTurnAria: t(msg`You said`),
    stopSpeaking: t(msg`Stop`),
  };

  return (
    <LiveAvatarCoach
      getSessionToken={getSessionToken}
      reportSession={reportSession}
      reportClientEvent={reportClientEvent}
      copy={copy}
      onError={onError}
      avatarSrc={coachAvatarWebp}
      pageContext={pageContext}
      launcherSlotSelector={COACH_LAUNCHER_SLOT}
      panelOffsetClassName={BOTTOM_BAR_PANEL_OFFSET}
      trapFocus={false}
    />
  );
}

export default LiveAvatarCoachMount;
